import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Shield, Users, MapPin, Building, Building2, Shovel, Image as ImageIcon, 
  Plus, Loader2, Mail, Megaphone, Trash2, Eye, EyeOff, ExternalLink, Pencil, FileText, Upload, KeyRound, Copy, Check
} from 'lucide-react';

import { API_URL } from '../config/api';

const TYPE_PIECES = [
  { value: 'CNI', label: 'Carte Nationale d\'Identité' },
  { value: 'PASSEPORT', label: 'Passeport' },
  { value: 'PERMIS', label: 'Permis de conduire' },
  { value: 'AUTRE', label: 'Autre document officiel' }
];

export default function Admin() {
  const navigate = useNavigate();
  const { user, token, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [bannieres, setBannieres] = useState([]);
  const [villes, setVilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Form states
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [banniereDialogOpen, setBanniereDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingBanniere, setEditingBanniere] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [uploadingPiece, setUploadingPiece] = useState(false);
  const pieceInputRef = useRef(null);
  
  // Password reset state
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  
  const [newUser, setNewUser] = useState({ email: '', password: '', nom: '', prenom: '', telephone: '', roles: ['AGENT'], date_naissance: '' });
  const [editUserData, setEditUserData] = useState({ nom: '', prenom: '', telephone: '', type_piece: '', date_expiration_piece: '', roles: ['CLIENT'] });
  const [newSite, setNewSite] = useState({ nom: '', description: '', ville: '', latitude: '', longitude: '', type_document: '', photos: [], video_url: '' });
  const [newBanniere, setNewBanniere] = useState({
    titre: '',
    description: '',
    image: '',
    numero_telephone: '',
    email: '',
    url_boutique: '',
    date_debut: new Date().toISOString().split('T')[0],
    date_fin: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    actif: true
  });

  useEffect(() => {
    // Wait for auth to finish loading before checking admin status
    if (authLoading) return;
    
    // If no user after loading, redirect to home
    if (!user) {
      navigate('/');
      return;
    }
    
    // Check if user has admin role
    if (!isAdmin()) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, token, authLoading]);

  const fetchData = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [statsRes, usersRes, sitesRes, villesRes, bannRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/users`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/sites`),
        fetch(`${API_URL}/api/villes`),
        fetch(`${API_URL}/api/bannieres`)
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      setSites(await sitesRes.json());
      setVilles(await villesRes.json());
      setBannieres(await bannRes.json());
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    setCreating(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/admin/users?roles=${newUser.roles.join(',')}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          email: newUser.email,
          password: newUser.password,
          nom: newUser.nom,
          prenom: newUser.prenom,
          telephone: newUser.telephone
        })
      });

      if (!response.ok) {
        const error = await response.json();
        const errorMsg = typeof error.detail === 'string' ? error.detail : 
                        Array.isArray(error.detail) ? error.detail.map(e => e.msg || e).join(', ') :
                        'Erreur lors de la création';
        throw new Error(errorMsg);
      }

      toast.success('Utilisateur créé');
      setUserDialogOpen(false);
      setNewUser({ email: '', password: '', nom: '', prenom: '', telephone: '', roles: ['AGENT'] });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateSite = async () => {
    setCreating(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/sites`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          ...newSite,
          latitude: newSite.latitude ? parseFloat(newSite.latitude) : null,
          longitude: newSite.longitude ? parseFloat(newSite.longitude) : null,
          photos: newSite.photos.filter(p => p.trim())
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success('Site créé');
      setSiteDialogOpen(false);
      setNewSite({ nom: '', description: '', ville: '', latitude: '', longitude: '', type_document: '', photos: [], video_url: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateOrUpdateBanniere = async () => {
    setCreating(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const url = editingBanniere 
        ? `${API_URL}/api/bannieres/${editingBanniere.banniere_id}`
        : `${API_URL}/api/bannieres`;
      
      const response = await fetch(url, {
        method: editingBanniere ? 'PUT' : 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(newBanniere)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success(editingBanniere ? 'Bannière mise à jour' : 'Bannière créée');
      setBanniereDialogOpen(false);
      setEditingBanniere(null);
      resetBanniereForm();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleBanniere = async (banniere) => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/bannieres/${banniere.banniere_id}/toggle`, {
        method: 'PATCH',
        headers,
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur');
      
      toast.success(banniere.actif ? 'Bannière désactivée' : 'Bannière activée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteBanniere = async (banniereId) => {
    if (!window.confirm('Supprimer cette bannière ?')) return;
    
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/bannieres/${banniereId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur');
      
      toast.success('Bannière supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditBanniere = (banniere) => {
    setEditingBanniere(banniere);
    setNewBanniere({
      titre: banniere.titre,
      description: banniere.description,
      image: banniere.image,
      numero_telephone: banniere.numero_telephone || '',
      email: banniere.email || '',
      url_boutique: banniere.url_boutique || '',
      date_debut: banniere.date_debut,
      date_fin: banniere.date_fin,
      actif: banniere.actif
    });
    setBanniereDialogOpen(true);
  };

  const resetBanniereForm = () => {
    setNewBanniere({
      titre: '',
      description: '',
      image: '',
      numero_telephone: '',
      email: '',
      url_boutique: '',
      date_debut: new Date().toISOString().split('T')[0],
      date_fin: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      actif: true
    });
  };

  const handleUpdateUserRole = async (userId, newRoles) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/roles?roles=${newRoles.join(',')}`, {
        method: 'PUT',
        headers,
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Erreur');
      toast.success('Rôles mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setEditUserData({
      nom: u.nom || '',
      prenom: u.prenom || '',
      telephone: u.telephone || '',
      type_piece: u.type_piece || '',
      date_expiration_piece: u.date_expiration_piece || '',
      roles: u.roles || ['CLIENT']
    });
    setEditUserDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    setCreating(true);
    
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/admin/users/${editingUser.user_id}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(editUserData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur');
      }
      
      toast.success('Utilisateur mis à jour');
      setEditUserDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur');
      }
      
      toast.success('Utilisateur supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUploadPieceIdentite = async (e) => {
    const file = e.target.files[0];
    if (!file || !editingUser) return;
    
    if (!editUserData.type_piece) {
      toast.error('Veuillez d\'abord sélectionner le type de pièce');
      return;
    }
    
    setUploadingPiece(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('type_piece', editUserData.type_piece);
      if (editUserData.date_expiration_piece) {
        formDataUpload.append('date_expiration', editUserData.date_expiration_piece);
      }
      
      const response = await fetch(`${API_URL}/api/admin/users/${editingUser.user_id}/piece-identite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur');
      }
      
      toast.success('Pièce d\'identité téléchargée');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingPiece(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editingUser) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir réinitialiser le mot de passe de ${editingUser.prenom} ${editingUser.nom} ?`)) {
      return;
    }
    
    setResettingPassword(true);
    setResetPasswordResult(null);
    setCopiedPassword(false);
    
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/admin/users/${editingUser.user_id}/reset-password`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur');
      }
      
      const result = await response.json();
      setResetPasswordResult(result);
      toast.success('Mot de passe réinitialisé');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCopyPassword = () => {
    if (resetPasswordResult?.new_password) {
      navigator.clipboard.writeText(resetPasswordResult.new_password);
      setCopiedPassword(true);
      toast.success('Mot de passe copié !');
      setTimeout(() => setCopiedPassword(false), 3000);
    }
  };

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect if not admin (after auth loading is complete)
  if (!user || !isAdmin()) {
    return null; // Will redirect via useEffect
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-bold font-serif">Administration</h1>
            <p className="text-muted-foreground">Gérez votre plateforme JAMM IMMO</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 flex-wrap">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Tableau de bord</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Utilisateurs</TabsTrigger>
            <TabsTrigger value="sites" data-testid="tab-sites">Sites</TabsTrigger>
            <TabsTrigger value="bannieres" data-testid="tab-bannieres">Bannières</TabsTrigger>
            <TabsTrigger value="creation" data-testid="tab-creation">Création rapide</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 text-primary" />
                    <div>
                      <div className="text-2xl font-bold">{stats?.users || 0}</div>
                      <div className="text-sm text-muted-foreground">Utilisateurs</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-8 h-8 text-primary" />
                    <div>
                      <div className="text-2xl font-bold">{stats?.sites || 0}</div>
                      <div className="text-sm text-muted-foreground">Sites</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Building className="w-8 h-8 text-primary" />
                    <div>
                      <div className="text-2xl font-bold">{stats?.logements || 0}</div>
                      <div className="text-sm text-muted-foreground">Logements</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-8 h-8 text-yellow-500" />
                    <div>
                      <div className="text-2xl font-bold">{stats?.contact_requests_new || 0}</div>
                      <div className="text-sm text-muted-foreground">Nouvelles demandes</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-8 h-8 text-secondary" />
                    <div>
                      <div className="text-2xl font-bold">{stats?.cooperatives || 0}</div>
                      <div className="text-sm text-muted-foreground">Coopératives actives</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Shovel className="w-8 h-8 text-secondary" />
                    <div>
                      <div className="text-2xl font-bold">{stats?.terrassements || 0}</div>
                      <div className="text-sm text-muted-foreground">Terrassements actifs</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Gestion des utilisateurs</CardTitle>
                <Button onClick={() => setUserDialogOpen(true)} data-testid="create-user-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvel utilisateur
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Téléphone</TableHead>
                      <TableHead>Pièce ID</TableHead>
                      <TableHead>Rôles</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                        <TableCell className="font-medium">{u.prenom} {u.nom}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.telephone || '-'}</TableCell>
                        <TableCell>
                          {u.id_card_uploaded ? (
                            <Badge className="bg-green-100 text-green-800">
                              <FileText className="w-3 h-3 mr-1" />
                              {u.type_piece || 'Fournie'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600">Non fournie</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {u.roles?.map(role => (
                              <Badge key={role} variant="outline">{role}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleEditUser(u)}
                              data-testid={`edit-user-${u.user_id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-red-600"
                              onClick={() => handleDeleteUser(u.user_id)}
                              disabled={u.user_id === user?.user_id}
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sites Tab */}
          <TabsContent value="sites">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Gestion des sites</CardTitle>
                <Button onClick={() => setSiteDialogOpen(true)} data-testid="create-site-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau site
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom du site</TableHead>
                      <TableHead>Ville</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Type document</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sites.map(s => (
                      <TableRow key={s.site_id} data-testid={`site-row-${s.site_id}`}>
                        <TableCell className="font-medium">{s.nom || s.ville}</TableCell>
                        <TableCell>{s.ville}</TableCell>
                        <TableCell className="max-w-xs truncate">{s.description}</TableCell>
                        <TableCell>{s.type_document?.replace('_', ' ') || '-'}</TableCell>
                        <TableCell>
                          <Badge className={s.archive ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'}>
                            {s.archive ? 'Archivé' : 'Actif'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bannieres Tab */}
          <TabsContent value="bannieres">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5" />
                  Gestion des bannières publicitaires
                </CardTitle>
                <Button onClick={() => { resetBanniereForm(); setEditingBanniere(null); setBanniereDialogOpen(true); }} data-testid="create-banniere-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle bannière
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {bannieres.map(b => (
                    <Card key={b.banniere_id} className={`p-4 ${!b.actif ? 'opacity-60' : ''}`} data-testid={`banniere-row-${b.banniere_id}`}>
                      <div className="flex gap-4">
                        <img 
                          src={b.image} 
                          alt={b.titre} 
                          className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold">{b.titre}</h3>
                              <p className="text-sm text-muted-foreground line-clamp-2">{b.description}</p>
                            </div>
                            <Badge className={b.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {b.actif ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                            <span>Du {b.date_debut} au {b.date_fin}</span>
                            {b.numero_telephone && <span>• {b.numero_telephone}</span>}
                            {b.url_boutique && (
                              <a href={b.url_boutique} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                                <ExternalLink className="w-3 h-3" />
                                Site web
                              </a>
                            )}
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button size="sm" variant="outline" onClick={() => handleEditBanniere(b)} data-testid={`edit-banniere-${b.banniere_id}`}>
                              Modifier
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleToggleBanniere(b)} data-testid={`toggle-banniere-${b.banniere_id}`}>
                              {b.actif ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                              {b.actif ? 'Désactiver' : 'Activer'}
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteBanniere(b.banniere_id)} data-testid={`delete-banniere-${b.banniere_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                  {bannieres.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Megaphone className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Aucune bannière publicitaire</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Creation Tab */}
          <TabsContent value="creation">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setUserDialogOpen(true)}>
                <CardContent className="p-6 text-center">
                  <Users className="w-12 h-12 mx-auto text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Créer un utilisateur</h3>
                  <p className="text-sm text-muted-foreground">Agent ou administrateur</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSiteDialogOpen(true)}>
                <CardContent className="p-6 text-center">
                  <MapPin className="w-12 h-12 mx-auto text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Créer un site</h3>
                  <p className="text-sm text-muted-foreground">Nouveau lotissement</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => { resetBanniereForm(); setEditingBanniere(null); setBanniereDialogOpen(true); }}>
                <CardContent className="p-6 text-center">
                  <Megaphone className="w-12 h-12 mx-auto text-primary mb-4" />
                  <h3 className="font-semibold mb-2">Créer une bannière</h3>
                  <p className="text-sm text-muted-foreground">Publicité sur le site</p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-lg transition-shadow opacity-50">
                <CardContent className="p-6 text-center">
                  <Building className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">Créer un logement</h3>
                  <p className="text-sm text-muted-foreground">Bientôt disponible</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Create User Dialog */}
        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un utilisateur</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom *</Label>
                  <Input
                    value={newUser.prenom}
                    onChange={(e) => setNewUser({ ...newUser, prenom: e.target.value })}
                    data-testid="user-prenom-input"
                    placeholder="Ex: Moussa"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={newUser.nom}
                    onChange={(e) => setNewUser({ ...newUser, nom: e.target.value })}
                    data-testid="user-nom-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Date de naissance *</Label>
                <Input
                  type="date"
                  value={newUser.date_naissance}
                  onChange={(e) => {
                    const year = e.target.value ? new Date(e.target.value).getFullYear() : '';
                    const prenom = newUser.prenom ? newUser.prenom.charAt(0).toUpperCase() + newUser.prenom.slice(1).toLowerCase() : '';
                    setNewUser({ 
                      ...newUser, 
                      date_naissance: e.target.value,
                      password: prenom && year ? `${prenom}${year}` : ''
                    });
                  }}
                  data-testid="user-date-naissance-input"
                />
                <p className="text-xs text-muted-foreground">
                  Mot de passe généré : <strong>{newUser.password || 'Prénom + Année'}</strong> (ex: Moussa1986)
                </p>
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={newUser.telephone}
                  onChange={(e) => setNewUser({ ...newUser, telephone: e.target.value })}
                  data-testid="user-telephone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select value={newUser.roles[0]} onValueChange={(v) => setNewUser({ ...newUser, roles: [v] })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CLIENT">Client</SelectItem>
                    <SelectItem value="AGENT">Agent</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreateUser} disabled={creating || !newUser.prenom || !newUser.email || !newUser.password} data-testid="create-user-submit">
                {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Site Dialog */}
        <Dialog open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Créer un site</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label>Nom du site *</Label>
                <Input
                  value={newSite.nom}
                  onChange={(e) => setNewSite({ ...newSite, nom: e.target.value })}
                  placeholder="Ex: Cité les Palmiers, Résidence Teranga..."
                  data-testid="site-nom-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Ville *</Label>
                <Select value={newSite.ville} onValueChange={(v) => setNewSite({ ...newSite, ville: v })}>
                  <SelectTrigger data-testid="site-ville-select">
                    <SelectValue placeholder="Sélectionner une ville" />
                  </SelectTrigger>
                  <SelectContent>
                    {villes.map(ville => (
                      <SelectItem key={ville} value={ville}>{ville}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newSite.description}
                  onChange={(e) => setNewSite({ ...newSite, description: e.target.value })}
                  rows={3}
                  data-testid="site-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={newSite.latitude}
                    onChange={(e) => setNewSite({ ...newSite, latitude: e.target.value })}
                    data-testid="site-latitude-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={newSite.longitude}
                    onChange={(e) => setNewSite({ ...newSite, longitude: e.target.value })}
                    data-testid="site-longitude-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Type de document</Label>
                <Select value={newSite.type_document} onValueChange={(v) => setNewSite({ ...newSite, type_document: v })}>
                  <SelectTrigger data-testid="site-type-document-select">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deliberation_mairie">Délibération mairie</SelectItem>
                    <SelectItem value="bail">Bail</SelectItem>
                    <SelectItem value="titre_foncier">Titre foncier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Photos</Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    const newPhotos = [];
                    for (const file of files) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error(`${file.name} trop volumineux (max 5MB)`);
                        continue;
                      }
                      const reader = new FileReader();
                      const dataUrl = await new Promise((resolve) => {
                        reader.onload = (event) => resolve(event.target.result);
                        reader.readAsDataURL(file);
                      });
                      newPhotos.push(dataUrl);
                    }
                    setNewSite({ ...newSite, photos: [...newSite.photos, ...newPhotos] });
                  }}
                  data-testid="site-photos-upload"
                />
                {newSite.photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newSite.photos.map((photo, idx) => (
                      <div key={idx} className="relative">
                        <img src={photo} alt={`Photo ${idx + 1}`} className="w-16 h-16 object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => setNewSite({ ...newSite, photos: newSite.photos.filter((_, i) => i !== idx) })}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Vidéo YouTube du site (optionnel)</Label>
                <Input
                  type="url"
                  value={newSite.video_url}
                  onChange={(e) => setNewSite({ ...newSite, video_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  data-testid="site-video-url-input"
                />
                <p className="text-xs text-muted-foreground">Collez le lien YouTube (watch, youtu.be, shorts ou embed).</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSiteDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreateSite} disabled={creating || !newSite.nom?.trim() || !newSite.ville} data-testid="create-site-submit">
                {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Banniere Dialog */}
        <Dialog open={banniereDialogOpen} onOpenChange={(open) => { setBanniereDialogOpen(open); if (!open) setEditingBanniere(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingBanniere ? 'Modifier la bannière' : 'Créer une bannière'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={newBanniere.titre}
                  onChange={(e) => setNewBanniere({ ...newBanniere, titre: e.target.value })}
                  placeholder="Ex: Promo Exceptionnelle"
                  data-testid="banniere-titre-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={newBanniere.description}
                  onChange={(e) => setNewBanniere({ ...newBanniere, description: e.target.value })}
                  rows={3}
                  placeholder="Description de la promotion..."
                  data-testid="banniere-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Image *</Label>
                <div className="flex flex-col gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('Image trop volumineuse (max 5MB)');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setNewBanniere({ ...newBanniere, image: event.target.result });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    data-testid="banniere-image-upload"
                  />
                  <p className="text-xs text-muted-foreground">Ou entrez une URL :</p>
                  <Input
                    value={newBanniere.image?.startsWith('data:') ? '' : newBanniere.image}
                    onChange={(e) => setNewBanniere({ ...newBanniere, image: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    data-testid="banniere-image-input"
                  />
                </div>
                {newBanniere.image && (
                  <img src={newBanniere.image} alt="Preview" className="w-32 h-32 object-cover rounded-lg mt-2" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={newBanniere.numero_telephone}
                    onChange={(e) => setNewBanniere({ ...newBanniere, numero_telephone: e.target.value })}
                    placeholder="+221 77 000 00 00"
                    data-testid="banniere-telephone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newBanniere.email}
                    onChange={(e) => setNewBanniere({ ...newBanniere, email: e.target.value })}
                    placeholder="contact@example.com"
                    data-testid="banniere-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>URL de la boutique / site web</Label>
                <Input
                  value={newBanniere.url_boutique}
                  onChange={(e) => setNewBanniere({ ...newBanniere, url_boutique: e.target.value })}
                  placeholder="https://www.boutique.com"
                  data-testid="banniere-url-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de début *</Label>
                  <Input
                    type="date"
                    value={newBanniere.date_debut}
                    onChange={(e) => setNewBanniere({ ...newBanniere, date_debut: e.target.value })}
                    data-testid="banniere-date-debut-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin *</Label>
                  <Input
                    type="date"
                    value={newBanniere.date_fin}
                    onChange={(e) => setNewBanniere({ ...newBanniere, date_fin: e.target.value })}
                    data-testid="banniere-date-fin-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newBanniere.actif}
                  onCheckedChange={(v) => setNewBanniere({ ...newBanniere, actif: v })}
                  data-testid="banniere-actif-switch"
                />
                <Label>Bannière active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBanniereDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreateOrUpdateBanniere} disabled={creating} data-testid="create-banniere-submit">
                {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingBanniere ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editUserDialogOpen} onOpenChange={(open) => { 
          setEditUserDialogOpen(open); 
          if (!open) { 
            setEditingUser(null); 
            setResetPasswordResult(null); 
            setCopiedPassword(false); 
          } 
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Modifier l'utilisateur</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input
                    value={editUserData.prenom}
                    onChange={(e) => setEditUserData({ ...editUserData, prenom: e.target.value })}
                    data-testid="edit-user-prenom-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input
                    value={editUserData.nom}
                    onChange={(e) => setEditUserData({ ...editUserData, nom: e.target.value })}
                    data-testid="edit-user-nom-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={editingUser?.email || ''}
                  disabled
                  className="bg-muted"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={editUserData.telephone}
                  onChange={(e) => setEditUserData({ ...editUserData, telephone: e.target.value })}
                  data-testid="edit-user-telephone-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select 
                  value={editUserData.roles[0]} 
                  onValueChange={(v) => setEditUserData({ ...editUserData, roles: [v] })}
                >
                  <SelectTrigger data-testid="edit-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CLIENT">Client</SelectItem>
                    <SelectItem value="AGENT">Agent</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Pièce d&#39;identité
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type de pièce</Label>
                    <Select 
                      value={editUserData.type_piece} 
                      onValueChange={(v) => setEditUserData({ ...editUserData, type_piece: v })}
                    >
                      <SelectTrigger data-testid="edit-user-type-piece-select">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {TYPE_PIECES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Date d'expiration</Label>
                    <Input
                      type="date"
                      value={editUserData.date_expiration_piece}
                      onChange={(e) => setEditUserData({ ...editUserData, date_expiration_piece: e.target.value })}
                      data-testid="edit-user-date-expiration-input"
                    />
                  </div>
                </div>
                
                <div className="mt-4 flex items-center gap-4">
                  <input
                    ref={pieceInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                    onChange={handleUploadPieceIdentite}
                    className="hidden"
                    data-testid="edit-user-piece-input"
                  />
                  
                  <Button 
                    variant="outline"
                    onClick={() => pieceInputRef.current?.click()}
                    disabled={uploadingPiece || !editUserData.type_piece}
                    data-testid="edit-user-upload-piece-btn"
                  >
                    {uploadingPiece ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Télécharger la pièce
                  </Button>
                  
                  {editingUser?.id_card_uploaded && (
                    <Badge className="bg-green-100 text-green-800">
                      Pièce déjà fournie
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Password Reset Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <KeyRound className="w-4 h-4" />
                  Réinitialisation du mot de passe
                </h4>
                
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-muted-foreground">
                    Générez un nouveau mot de passe pour cet utilisateur. Le mot de passe sera affiché pour que vous puissiez le lui communiquer.
                  </p>
                  
                  <Button 
                    variant="outline"
                    onClick={handleResetPassword}
                    disabled={resettingPassword}
                    className="w-fit"
                    data-testid="reset-password-btn"
                  >
                    {resettingPassword ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <KeyRound className="w-4 h-4 mr-2" />
                    )}
                    Réinitialiser le mot de passe
                  </Button>
                  
                  {resetPasswordResult && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-2">
                      <p className="text-sm text-green-800 font-medium mb-2">
                        Nouveau mot de passe généré pour {resetPasswordResult.user_nom}
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="bg-white px-3 py-2 rounded border text-lg font-mono flex-1">
                          {resetPasswordResult.new_password}
                        </code>
                        <Button 
                          size="sm" 
                          variant={copiedPassword ? "default" : "outline"}
                          onClick={handleCopyPassword}
                          data-testid="copy-password-btn"
                        >
                          {copiedPassword ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Copié
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-1" />
                              Copier
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Email: {resetPasswordResult.user_email}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditUserDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleUpdateUser} disabled={creating} data-testid="edit-user-submit">
                {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
